import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";

export type Market = {
  id: string;
  tenant_id: string;
  name: string;
  meta_data: Record<string, any>;
  fips_code: string;
};

export type MultiMarketPickerProps = {
  selectedMarketIds?: string[];
  readonlyMarketIds?: string[]; // IDs that cannot be removed (e.g., from location)
  onPick: (marketIds: string[]) => void;
  onCancel?: () => void;
};

export function MultiMarketPicker({
  selectedMarketIds = [],
  readonlyMarketIds = [],
  onPick,
  onCancel,
}: MultiMarketPickerProps) {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filter out readonly IDs from initial selection (they're managed separately)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const readonlySet = new Set(readonlyMarketIds);
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedMarketIds.filter((id) => !readonlySet.has(id)));

  // Fetch markets on mount
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("markets")
          .select("id, tenant_id, name, meta_data, fips_code")
          .order("name", { ascending: true });

        if (cancel) return;

        if (fetchError) {
          console.error("Failed to fetch markets:", fetchError);
          setError("Failed to load markets");
          return;
        }

        setMarkets(data ?? []);
      } catch (err) {
        if (!cancel) {
          console.error("Unexpected error fetching markets:", err);
          setError("An unexpected error occurred");
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (!search) return markets;
    const searchLower = search.toLowerCase();
    return markets.filter(
      (market) =>
        market.name.toLowerCase().includes(searchLower) || market.fips_code.toLowerCase().includes(searchLower),
    );
  }, [markets, search]);

  const toggleMarket = (id: string) => {
    // Prevent toggling readonly markets
    if (readonlySet.has(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Get readonly markets for display
  const readonlyMarkets = useMemo(() => {
    return markets.filter((m) => readonlySet.has(m.id));
  }, [markets, readonlySet]);

  const selectedMarkets = useMemo(() => {
    return markets.filter((m) => selectedIds.includes(m.id));
  }, [markets, selectedIds]);

  return (
    <View testID="multi-market-picker" style={styles.container}>
      <ThemedText type="subtitle">Select Markets</ThemedText>

      <TextInput
        testID="multi-market-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search markets..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
        editable={!loading}
      />

      {/* Selected chips - readonly first, then editable */}
      {(readonlyMarkets.length > 0 || selectedMarkets.length > 0) && (
        <View style={styles.chipContainer}>
          {/* Readonly chips (no X button, dashed border) */}
          {readonlyMarkets.map((market) => (
            <View
              key={market.id}
              style={[
                styles.chip,
                {
                  backgroundColor: "transparent",
                  borderWidth: 1,
                  borderStyle: "dashed",
                  borderColor: colors.primary,
                },
              ]}
            >
              <ThemedText style={[styles.chipText, { color: colors.primary }]}>{market.name} · Location</ThemedText>
            </View>
          ))}
          {/* Editable chips (with X button) */}
          {selectedMarkets.map((market) => (
            <Pressable
              key={market.id}
              style={[styles.chip, { backgroundColor: colors.primary }]}
              onPress={() => toggleMarket(market.id)}
            >
              <ThemedText style={styles.chipText}>{market.name}</ThemedText>
              <MaterialCommunityIcons name="close" size={14} color="white" />
            </Pressable>
          ))}
        </View>
      )}

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading...</ThemedText>
        ) : error ? (
          <ThemedText style={[styles.message, { color: colors.red.text }]}>{error}</ThemedText>
        ) : filteredItems.length > 0 ? (
          filteredItems.map((market) => {
            const isReadonly = readonlySet.has(market.id);
            const isSelected = selectedIds.includes(market.id) || isReadonly;
            return (
              <Pressable
                key={market.id}
                testID={`market-option-${market.id}`}
                style={[
                  styles.itemRow,
                  { borderBottomColor: colors.border },
                  isSelected && { backgroundColor: colors.backgroundHover },
                  isReadonly && { opacity: 0.7 },
                ]}
                onPress={() => toggleMarket(market.id)}
                disabled={isReadonly}
              >
                <MaterialCommunityIcons
                  name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                  size={24}
                  color={isSelected ? colors.primary : colors.text}
                />
                <View style={styles.itemContent}>
                  <View style={styles.headerRow}>
                    <ThemedText type="defaultSemiBold">
                      {market.name}
                      {isReadonly && " (From Location)"}
                    </ThemedText>
                    {market.fips_code && <ThemedText style={styles.fipsCode}>FIPS: {market.fips_code}</ThemedText>}
                  </View>
                </View>
              </Pressable>
            );
          })
        ) : (
          <ThemedText style={styles.message}>{search ? "No markets found" : "No markets available"}</ThemedText>
        )}
      </ScrollView>

      <View style={styles.footer}>
        {onCancel && (
          <Button
            testID="multi-market-picker-cancel"
            text="Cancel"
            style={[styles.cancelButton, { borderColor: colors.border }]}
            textStyle={{ color: colors.text }}
            onPress={onCancel}
          />
        )}
        <Button
          testID="multi-market-picker-confirm"
          text={`Confirm${selectedIds.length + readonlyMarketIds.length > 0 ? ` (${selectedIds.length + readonlyMarketIds.length})` : ""}`}
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          onPress={() => onPick([...readonlyMarketIds, ...selectedIds])}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
    minWidth: 350,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  chipContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
  },
  chipText: {
    fontSize: 12,
    color: "white",
    fontWeight: "500",
  },
  scrollView: {
    maxHeight: 280,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 8,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  itemContent: {
    flex: 1,
    gap: 2,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  fipsCode: {
    fontSize: 12,
    color: "#4169E1",
    fontWeight: "500",
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
